import { Alert, Button, ColorPicker, Flex, Form, Space, Typography, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  useOrganizationConfigs,
  useResetOrganizationConfigs,
  useSetOrganizationConfigs,
} from '../../api/Onboarding';
import { ConfigKey } from '../../constants';
import { defaultChartColors, presetChartColors } from '../../styles';

const { Text } = Typography;

const PresetColor = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  background-color: ${(props) => props.color};
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const StyledText = styled(Text)`
  font-size: 12px;
`;

const StyledAlert = styled(Alert)`
  max-width: 800px;
`;

function ChartConfiguration({ organizationId }) {
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);

  const { isFetching: configsFetching } = useOrganizationConfigs(
    { organizationId },
    {
      onSuccess: (data) => {
        if (data.data) {
          form.setFieldsValue({
            [ConfigKey.PRIMARY_CHART_COLOR]:
              data.data[ConfigKey.PRIMARY_CHART_COLOR] ||
              defaultChartColors[ConfigKey.PRIMARY_CHART_COLOR],
            [ConfigKey.SECONDARY_CHART_COLOR]:
              data.data[ConfigKey.SECONDARY_CHART_COLOR] ||
              defaultChartColors[ConfigKey.SECONDARY_CHART_COLOR],
          });
        }
      },
    },
  );

  const { mutate: setConfigs, isLoading: setConfigsLoading } = useSetOrganizationConfigs();
  const { mutate: resetConfigs, isLoading: resetConfigsLoading } = useResetOrganizationConfigs();

  const handleSelectPreset = (colorKey, color) => {
    form.setFieldsValue({ [colorKey]: color });
    setHasChanges(true);
  };

  const handleSave = (values) => {
    setConfigs(
      { organizationId, data: values },
      {
        onSuccess: () => {
          message.success('Chart colors saved successfully');
          setHasChanges(false);
        },
      },
    );
  };

  const handleResetToDefault = () => {
    // Reset by calling DELETE endpoint
    resetConfigs(
      { organizationId },
      {
        onSuccess: () => {
          message.success('Chart colors reset to default');
          setHasChanges(false);
        },
      },
    );
  };

  if (configsFetching) {
    return <Text type="secondary">Loading chart configuration...</Text>;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
      autoComplete="off"
      requiredMark={false}
      onValuesChange={() => setHasChanges(true)}
    >
      <Flex vertical gap={24}>
        <Text type="secondary">Customize the colors used in your charts.</Text>
        <div>
          {[ConfigKey.PRIMARY_CHART_COLOR, ConfigKey.SECONDARY_CHART_COLOR].map((key) => (
            <Form.Item
              key={key}
              label={
                <Text strong>
                  {key === ConfigKey.PRIMARY_CHART_COLOR
                    ? 'Primary chart color'
                    : 'Secondary chart color'}
                </Text>
              }
            >
              <Flex align="center" gap={24}>
                <Form.Item name={key} getValueFromEvent={(c) => c.toHexString()} noStyle>
                  <ColorPicker disabledAlpha showText />
                </Form.Item>
                <Space>
                  <StyledText type="secondary">Quick presets:</StyledText>
                  <Flex gap={8} wrap>
                    {presetChartColors.map((color) => (
                      <PresetColor
                        key={`${key}-${color}`}
                        color={color}
                        onClick={() => handleSelectPreset(key, color)}
                      />
                    ))}
                  </Flex>
                </Space>
              </Flex>
            </Form.Item>
          ))}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={setConfigsLoading}
                disabled={!hasChanges}
              >
                Save chart colors
              </Button>
              <Button onClick={handleResetToDefault} loading={resetConfigsLoading}>
                Reset to default
              </Button>
            </Space>
          </Form.Item>
          <StyledAlert
            type="warning"
            message={
              <StyledText type="secondary">
                <strong>Note:</strong> Resetting to default will remove your custom color settings.
                The system will use the default color scheme for all charts.
              </StyledText>
            }
          />
        </div>
      </Flex>
    </Form>
  );
}

ChartConfiguration.propTypes = {
  organizationId: PropTypes.number.isRequired,
};

export default ChartConfiguration;
